import type {
  Section,
  SectionDef,
  Paragraph,
  Run,
  Table,
  TableRow,
  TableCell,
  Picture,
  CellPadding,
  PageMargin,
  ColumnDef,
  HeaderFooterDef,
  SubDocument,
} from '../../../types.js';
import {
  parseXml,
  findChild,
  findChildren,
  attrInt,
  attrStr,
  type XmlNode,
} from './parser.js';

export function parseSectionXml(xml: string): Section {
  const root = parseXml(xml);

  return {
    def: parseSectionDef(root),
    paragraphs: parseParagraphs(root),
  };
}

function parseSectionDef(root: XmlNode): SectionDef {
  // hs:secPr (우리 생성) 또는 hp:secPr (한글 — run 안에 있음)
  let secPr = findChild(root, 'hs:secPr');

  // 한글 파일: secPr이 첫 문단의 run 안에 있음
  if (!secPr) {
    for (const p of findChildren(root, 'hp:p')) {
      for (const run of findChildren(p, 'hp:run')) {
        const found = findChild(run, 'hp:secPr');
        if (found) { secPr = found; break; }
      }
      if (secPr) break;
    }
  }

  if (!secPr) {
    return defaultSectionDef();
  }

  const pagePr = findChild(secPr, 'hp:pagePr') || findChild(secPr, 'hs:pagePr');
  // 한글: pagePr > margin 자식 / 우리: hs:pageMargin 형제
  const pageMarginNode = findChild(secPr, 'hs:pageMargin')
    || (pagePr ? findChild(pagePr, 'hp:margin') || findChild(pagePr, 'hs:margin') : undefined);

  // 다단
  const colPr = findChild(secPr, 'hs:colPr');
  let columns: ColumnDef | undefined;
  if (colPr && attrInt(colPr, 'count', 1) > 1) {
    columns = {
      type: attrStr(colPr, 'type', 'NORMAL') as ColumnDef['type'],
      count: attrInt(colPr, 'count', 1),
      gap: attrInt(colPr, 'gap', 0),
      sameSizes: attrStr(colPr, 'sameSz') === '1',
    };
  }

  // 머리글/바닥글
  let headerFooter: HeaderFooterDef | undefined;
  const headerNode = findChild(secPr, 'hs:header');
  const footerNode = findChild(secPr, 'hs:footer');
  if (headerNode || footerNode) {
    headerFooter = {};
    if (headerNode) {
      headerFooter.header = { paragraphs: parseParagraphs(headerNode) };
    }
    if (footerNode) {
      headerFooter.footer = { paragraphs: parseParagraphs(footerNode) };
    }
  }

  return {
    pageWidth: pagePr ? attrInt(pagePr, 'width', 59528) : 59528,
    pageHeight: pagePr ? attrInt(pagePr, 'height', 84188) : 84188,
    landscape: pagePr ? (attrStr(pagePr, 'landscape') === 'LANDSCAPE' || attrStr(pagePr, 'landscape') === 'WIDELY') : false,
    gutterType: pagePr
      ? (attrStr(pagePr, 'gutterType', 'LEFT_ONLY') as any)
      : 'LEFT_ONLY',
    pageMargin: pageMarginNode
      ? parsePageMargin(pageMarginNode)
      : defaultPageMargin(),
    columns,
    headerFooter,
  };
}

function parsePageMargin(node: XmlNode): PageMargin {
  return {
    left: attrInt(node, 'left'),
    right: attrInt(node, 'right'),
    top: attrInt(node, 'top'),
    bottom: attrInt(node, 'bottom'),
    header: attrInt(node, 'header'),
    footer: attrInt(node, 'footer'),
    gutter: attrInt(node, 'gutter'),
  };
}

function parseParagraphs(parent: XmlNode): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  for (const pNode of findChildren(parent, 'hp:p')) {
    paragraphs.push(parseParagraph(pNode));
  }

  return paragraphs;
}

function parseParagraph(pNode: XmlNode): Paragraph {
  const runs: Run[] = [];

  for (const runNode of findChildren(pNode, 'hp:run')) {
    const charPrIDRef = attrInt(runNode, 'charPrIDRef');

    // Break elements
    const lineBreak = findChild(runNode, 'hp:lineBreak');
    const pageBreak = findChild(runNode, 'hp:pageBreak');
    const colBreak = findChild(runNode, 'hp:colBreak');

    if (lineBreak) {
      runs.push({ t: 'break', breakType: 'LINE', charPrIDRef });
    } else if (pageBreak) {
      runs.push({ t: 'break', breakType: 'PAGE', charPrIDRef });
    } else if (colBreak) {
      runs.push({ t: 'break', breakType: 'COLUMN', charPrIDRef });
    } else {
      // Table
      const tblNode = findChild(runNode, 'hp:tbl');
      if (tblNode) {
        runs.push({ t: 'table', table: parseTable(tblNode), charPrIDRef });
        continue;
      }

      // Picture
      const picNode = findChild(runNode, 'hp:pic');
      if (picNode) {
        runs.push({ t: 'picture', picture: parsePicture(picNode), charPrIDRef });
        continue;
      }

      // Text run — hp:runText (우리 생성) 또는 hp:t (한글 생성)
      const runTextNode = findChild(runNode, 'hp:runText') || findChild(runNode, 'hp:t');
      const text = runTextNode ? getTextContent(runTextNode) : '';
      runs.push({ t: 'text', text, charPrIDRef });
    }
  }

  const pageBreakVal = attrStr(pNode, 'pageBreak', '0');
  const pageBreak = pageBreakVal === '1' || pageBreakVal === 'true';

  return {
    paraPrIDRef: attrInt(pNode, 'paraPrIDRef'),
    styleIDRef: attrInt(pNode, 'styleIDRef'),
    runs,
    ...(pageBreak ? { pageBreak: true } : {}),
  };
}

// ─── Table Parsing ───

function parseTable(tblNode: XmlNode): Table {
  const rowCount = attrInt(tblNode, 'rowCnt', 1);
  const colCount = attrInt(tblNode, 'colCnt', 1);
  const borderFillIDRef = attrInt(tblNode, 'borderFillIDRef', 1);
  const cellSpacing = attrInt(tblNode, 'cellSpacing', 0);

  // 테이블 크기: 속성(우리) 또는 hp:sz 자식(한글)
  const szNode = findChild(tblNode, 'hp:sz');
  const width = szNode ? attrInt(szNode, 'width', 0) : attrInt(tblNode, 'width', 0);

  // colWidths: hp:colSz(우리) 또는 첫 행 셀들의 cellSz에서 추출(한글)
  const colSzNodes = findChildren(tblNode, 'hp:colSz');
  let colWidths = colSzNodes.map((n) => attrInt(n, 'width', 0));

  // rows
  const rows: TableRow[] = [];
  for (const trNode of findChildren(tblNode, 'hp:tr')) {
    rows.push(parseTableRow(trNode));
  }

  // colWidths가 없으면 첫 행 셀들에서 추출
  if (colWidths.length === 0 && rows.length > 0) {
    colWidths = rows[0].cells.map((c) => c.width);
  }

  return {
    rowCount,
    colCount,
    width,
    borderFillIDRef,
    cellSpacing: cellSpacing || undefined,
    colWidths,
    rows,
  };
}

function parseTableRow(trNode: XmlNode): TableRow {
  const cells: TableCell[] = [];

  for (const tcNode of findChildren(trNode, 'hp:tc')) {
    cells.push(parseTableCell(tcNode));
  }

  // 행 높이: 첫 셀의 높이로 대체 (한글은 tr에 height 없음)
  const height = attrInt(trNode, 'height', 0) || (cells.length > 0 ? cells[0].height : 0);

  return { height, cells };
}

function parseTableCell(tcNode: XmlNode): TableCell {
  // 셀 병합: 속성(우리) 또는 hp:cellSpan 자식(한글)
  const cellSpanNode = findChild(tcNode, 'hp:cellSpan');
  const colSpan = cellSpanNode
    ? attrInt(cellSpanNode, 'colSpan', 1)
    : attrInt(tcNode, 'colSpan', 1);
  const rowSpan = cellSpanNode
    ? attrInt(cellSpanNode, 'rowSpan', 1)
    : attrInt(tcNode, 'rowSpan', 1);

  // 셀 크기: 속성(우리) 또는 hp:cellSz 자식(한글)
  const cellSzNode = findChild(tcNode, 'hp:cellSz');
  const width = cellSzNode
    ? attrInt(cellSzNode, 'width', 0)
    : attrInt(tcNode, 'width', 0);
  const height = cellSzNode
    ? attrInt(cellSzNode, 'height', 0)
    : attrInt(tcNode, 'height', 0);

  const borderFillIDRef = attrInt(tcNode, 'borderFillIDRef', 1);

  // 셀 패딩: 속성(우리) 또는 hp:cellMargin 자식(한글)
  let padding: CellPadding | undefined;
  const cellMarginNode = findChild(tcNode, 'hp:cellMargin');
  if (cellMarginNode) {
    padding = {
      left: attrInt(cellMarginNode, 'left'),
      right: attrInt(cellMarginNode, 'right'),
      top: attrInt(cellMarginNode, 'top'),
      bottom: attrInt(cellMarginNode, 'bottom'),
    };
  } else if (tcNode.attrs['paddingLeft'] !== undefined) {
    padding = {
      left: attrInt(tcNode, 'paddingLeft'),
      right: attrInt(tcNode, 'paddingRight'),
      top: attrInt(tcNode, 'paddingTop'),
      bottom: attrInt(tcNode, 'paddingBottom'),
    };
  }

  // subList 안의 paragraphs
  const subList = findChild(tcNode, 'hp:subList');
  const paragraphs = subList ? parseParagraphs(subList) : [];

  return { paragraphs, colSpan, rowSpan, width, height, borderFillIDRef, padding };
}

// ─── Picture Parsing ───

function parsePicture(picNode: XmlNode): Picture {
  return {
    binDataIDRef: attrInt(picNode, 'binDataIDRef', 0),
    width: attrInt(picNode, 'width', 0),
    height: attrInt(picNode, 'height', 0),
    offsetX: picNode.attrs['offsetX'] !== undefined ? attrInt(picNode, 'offsetX') : undefined,
    offsetY: picNode.attrs['offsetY'] !== undefined ? attrInt(picNode, 'offsetY') : undefined,
  };
}

// ─── Helpers ───

function getTextContent(node: XmlNode): string {
  let text = node.text;
  for (const child of node.children) {
    text += getTextContent(child);
  }
  return unescapeXml(text);
}

function unescapeXml(str: string): string {
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

function defaultSectionDef(): SectionDef {
  return {
    pageWidth: 59528,
    pageHeight: 84188,
    pageMargin: defaultPageMargin(),
    landscape: false,
  };
}

function defaultPageMargin(): PageMargin {
  return {
    left: 8504,
    right: 8504,
    top: 7087,
    bottom: 7087,
    header: 4252,
    footer: 4252,
    gutter: 0,
  };
}
